import * as React from 'react';
import { Trans } from 'react-i18next';
import { styled, StyleObject, useStyletron } from 'styletron-react';

import { TechemTheme } from '@techem/techem-theme';
import { Col, Row } from 'react-bootstrap';
import CenteredLoadingIndicator from 'presentation/components/CenteredLoadingIndicator';
import ExternalLink from '../../components/ExternalLink';
import {
  DocumentIdentifier,
  waitForReturnPolicy
} from '../../../frameworks_drivers/localization/Localization';

const Paragraph = styled('p', {
  hyphens: 'auto',
  textAlign: 'justify'
});

const loadingView = (
  <div className="d-flex w-100 h-100 m-auto top-0 start-0" style={{ transitionDuration: '350ms' }}>
    <Row className="my-5 m-auto">
      <Col xs={12} className="d-flex my-3">
        <span className="m-auto">
          <Trans i18nKey="ReturnPolicyLoadingInfoText" />
        </span>
      </Col>
      <Col xs={12} style={{ minHeight: '300px' }}>
        <CenteredLoadingIndicator />
      </Col>
    </Row>
  </div>
);

const contentView = (css: (style: StyleObject) => string) => {
  return (
    <>
      <main>
        <section>
          <h3>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_1.title" />
          </h3>
          <Paragraph>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_1.paragraph_1" />
          </Paragraph>
          <Paragraph>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_1.paragraph_2" />
          </Paragraph>
          <Paragraph>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_1.paragraph_3" />
          </Paragraph>
          <Paragraph>
            <Trans
              ns={DocumentIdentifier.ReturnPolicy}
              i18nKey="content.section_1.paragraph_4"
              tOptions={{ skipPostProcessing: true }}
              components={{
                link: (
                  <ExternalLink
                    className={css({ color: TechemTheme.colors.primary })}
                    name="service@techem.de"
                    href="mailto:service@techem.de"
                  />
                )
              }}
            />
          </Paragraph>
          <Paragraph>
            <Trans
              ns={DocumentIdentifier.ReturnPolicy}
              i18nKey="content.section_1.paragraph_5"
              tOptions={{ skipPostProcessing: true }}
              components={{
                link: (
                  <ExternalLink
                    className={css({ color: TechemTheme.colors.primary })}
                    name="www.techem.de/widerruf"
                    href="https://www.techem.de/widerruf"
                  />
                )
              }}
            />
          </Paragraph>
          <Paragraph>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_1.paragraph_6" />
          </Paragraph>
        </section>
        <section>
          <h3>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_2.title" />
          </h3>
          <Paragraph>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_2.paragraph_1" />
          </Paragraph>
          <Paragraph>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_2.paragraph_2" />
          </Paragraph>
          <sup>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.section_2.superscript" />
          </sup>
        </section>
      </main>
      <footer className="d-flex flex-column justify-content-center py-3">
        <div className="text-center mt-3">
          <div>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.footer.name" />
          </div>
          <div>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.footer.address" />
          </div>
          <div>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.footer.postal" />
          </div>
          <div>
            <ExternalLink
              className={css({ color: TechemTheme.colors.primary })}
              name="www.techem.de"
              href="https://www.techem.de"
            />
          </div>
          <div>
            <Trans ns={DocumentIdentifier.ReturnPolicy} i18nKey="content.footer.status" />
          </div>
        </div>
      </footer>
    </>
  );
};

const ReturnPolicyContent: React.FC = () => {
  const [contentLoaded, setContentLoaded] = React.useState(false);
  const [css] = useStyletron();

  React.useEffect(() => {
    if (!contentLoaded) {
      const fetchData = async () => {
        waitForReturnPolicy.then((response: any) => {
          setContentLoaded(true);
        });
      };
      fetchData();
    }
  });

  return !contentLoaded ? loadingView : contentView(css);
};

export default ReturnPolicyContent;
